/**
 * Project seed data - Consistent pre-configured projects based on Axioma Project Process
 */

import { faker } from '@faker-js/faker'
import { Project, Workspace, Status, Priority, Horizon } from '@/types'

interface ProjectTemplate {
  name: string
  description: string
  status: Status
  priority: Priority
  horizon: Horizon
}

/**
 * Pre-defined projects based on Axioma Project Process document
 */
export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    name: 'Client Requirements & Contract Setup',
    description:
      'Identify stakeholders, gather requirements, schedule meetings, outline scope, determine timeline, and finalize contract.',
    status: Status.Done,
    priority: Priority.High,
    horizon: Horizon.Now,
  },
  {
    name: 'Website Design Implementation',
    description:
      'Set up breakpoints, typography, layout specifications, color palettes, spacing, and implement global design elements per style guide.',
    status: Status.InProgress,
    priority: Priority.High,
    horizon: Horizon.Now,
  },
  {
    name: 'Website Accessibility Audit',
    description:
      'Ensure WCAG 2.1 compliance, semantic HTML, proper alt text, keyboard navigation, ARIA roles, and screen reader compatibility.',
    status: Status.InProgress,
    priority: Priority.High,
    horizon: Horizon.Now,
  },
  {
    name: 'Web Performance Optimization',
    description:
      'Run Lighthouse tests, optimize assets, implement lazy loading, minify CSS/JavaScript, and ensure sub-3-second page loads.',
    status: Status.ToDo,
    priority: Priority.High,
    horizon: Horizon.Next,
  },
  {
    name: 'Cross-Browser Testing',
    description:
      'Test website functionality and layout across Chrome, Safari, Edge, Firefox, and real-world mobile devices.',
    status: Status.ToDo,
    priority: Priority.Medium,
    horizon: Horizon.Next,
  },
  {
    name: 'Developer Experience & Documentation',
    description:
      'Consider future maintainer needs, write comprehensive documentation on project architecture and tooling.',
    status: Status.ToDo,
    priority: Priority.Medium,
    horizon: Horizon.Next,
  },
  {
    name: 'User Experience Enhancement',
    description:
      'Ensure readable typography, useful 404 pages, loading indicators, password visibility toggles, and proper button actions.',
    status: Status.InProgress,
    priority: Priority.High,
    horizon: Horizon.Now,
  },
  {
    name: 'Design & Visual Consistency Review',
    description:
      'Verify consistent layout, fonts, spacing, color usage, iconography, and proper image resolution throughout the site.',
    status: Status.ToDo,
    priority: Priority.Medium,
    horizon: Horizon.Next,
  },
  {
    name: 'SEO Implementation',
    description:
      'Add social share images, favicon, unique titles/descriptions, proper header hierarchy, canonical tags, sitemap, and robots.txt.',
    status: Status.ToDo,
    priority: Priority.Medium,
    horizon: Horizon.Next,
  },
  {
    name: 'Copywriting & Content Review',
    description:
      'Spell-check and grammar-proof all text, ensure headlines describe solutions to visitor problems.',
    status: Status.ToDo,
    priority: Priority.Medium,
    horizon: Horizon.Next,
  },
  {
    name: 'Calls to Action Optimization',
    description:
      'Define and implement primary, secondary, and tertiary CTAs with proper placement and prominence throughout the site.',
    status: Status.Draft,
    priority: Priority.Low,
    horizon: Horizon.Someday,
  },
  {
    name: 'Marketing Analytics Setup',
    description:
      'Install and configure analytics software to track visitor behavior and conversions.',
    status: Status.Draft,
    priority: Priority.Medium,
    horizon: Horizon.Someday,
  },
  {
    name: 'DevOps & Monitoring',
    description:
      'Schedule automated backups and enable monitoring tools for the production environment.',
    status: Status.Draft,
    priority: Priority.High,
    horizon: Horizon.Someday,
  },
  {
    name: 'Security Implementation',
    description:
      'Enforce HTTPS, eliminate mixed-content warnings, protect inputs against XSS and SQL injection, secure password fields.',
    status: Status.ToDo,
    priority: Priority.High,
    horizon: Horizon.Next,
  },
  {
    name: 'Legal Compliance Setup',
    description: 'Implement cookie consent banners that meet GDPR and CCPA legal requirements.',
    status: Status.Draft,
    priority: Priority.Medium,
    horizon: Horizon.Someday,
  },
  {
    name: 'Website Launch',
    description:
      'Get domain access, draft and approve launch plan, execute launch, and monitor the live website.',
    status: Status.Draft,
    priority: Priority.High,
    horizon: Horizon.Someday,
  },
  {
    name: 'Component Quality Assurance',
    description:
      'QA testing for menus/navigation, forms, buttons, and dropdown menus to ensure proper functionality.',
    status: Status.InProgress,
    priority: Priority.High,
    horizon: Horizon.Now,
  },
  {
    name: 'Project Handoff & Documentation',
    description:
      'Hand off project to stakeholders with comprehensive documentation and README for tooling and systems.',
    status: Status.Draft,
    priority: Priority.Medium,
    horizon: Horizon.Someday,
  },
]

/**
 * Shuffle array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

/**
 * Generate consistent projects from templates and assign to workspaces
 */
export function generateProjectsForWorkspaces(workspaces: Workspace[]): Project[] {
  if (workspaces.length === 0) {
    throw new Error('At least one workspace is required')
  }

  const projects: Project[] = []

  // Shuffle project templates for random assignment
  const shuffledTemplates = shuffleArray(PROJECT_TEMPLATES)

  // Assign projects to workspaces in round-robin fashion
  shuffledTemplates.forEach((template, index) => {
    const workspace = workspaces[index % workspaces.length]
    const creator = workspace.users[0] // Use first user as creator

    // Select 2-4 users from workspace for the project
    const projectUserCount = Math.min(faker.number.int({ min: 2, max: 4 }), workspace.users.length)
    const projectUsers = workspace.users.slice(0, projectUserCount)

    const project: Project = {
      id: faker.string.uuid(),
      createdAt: faker.date.past({ years: 1 }),
      updatedAt: faker.date.recent({ days: 7 }),
      creator,
      name: template.name,
      description: template.description,
      status: template.status,
      priority: template.priority,
      horizon: template.horizon,
      workspace,
      users: projectUsers,
      tasks: [],
      messages: [],
      documents: [],
    }

    projects.push(project)
  })

  return projects
}
